// AdminDashboardParts.jsx
// Sub-components extracted from AdminDashboard.jsx to reduce its complexity
// (originally 502 lines / cyclomatic ~98). All pieces are presentational
// where possible — state lives in the parent <AdminDashboard/>.

import React from "react";

export const B = {
  gold: "#D4A843", blue: "#4A90D9", green: "#43B581", red: "#E74C3C",
  bg: "#0D0F13", card: "#13161C", border: "#1E222A",
  white: "#F0F0F0", whiteGray: "#A0A8B8", gray: "#6B7280", darkGray: "#2A2E36",
};

export const btnStyle  = { background: B.card, border: `1px solid ${B.border}`, borderRadius: 8, color: B.white, padding: "10px 20px", cursor: "pointer", fontSize: 14 };
export const btnSmall  = { background: "transparent", border: `1px solid ${B.border}`, borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12 };
export const btnTiny   = { background: "transparent", border: "1px solid", borderRadius: 4, padding: "3px 8px", cursor: "pointer", fontSize: 10, fontWeight: 600 };

const COURSE_LABEL = { beginner: "Beginner", advanced: "Advanced", cfd: "CFD", commodities: "Commod.", roadmap: "Roadmap" };
const COURSE_COLOR = { beginner: B.blue, advanced: B.gold, cfd: "#00D4AA", commodities: "#E8883A", roadmap: "#9B59B6" };
const COURSES = ["beginner", "advanced", "cfd", "commodities", "roadmap"];

// ── Pill — small clickable badge ───────────────────────────────────────
export const Pill = ({ active, color, children, onClick, ...rest }) => (
  <span data-testid={`pill-${children?.toString().toLowerCase().replace(/\s/g,'-')}`}
    onClick={onClick} {...rest} style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 10px", borderRadius: 50, fontSize: 11, fontWeight: 600, cursor: onClick ? "pointer" : "default",
    background: active ? color + "25" : "transparent",
    border: `1px solid ${active ? color : B.border}`,
    color: active ? color : B.gray,
  }}>{children}</span>
);

// ── Header bar (logo + Analytics/Home/Logout) ──────────────────────────
export function HeaderBar({ onAnalytics, onHome, onLogout }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: `1px solid ${B.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <img src="/flexx-logo.png" alt="FLEXX" style={{ height: 28 }} onError={e => { e.target.style.display = "none"; }} />
        <span style={{ fontSize: 11, letterSpacing: 2, color: B.gold, fontFamily: "monospace" }}>ADMIN DASHBOARD</span>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button data-testid="admin-analytics-btn" onClick={onAnalytics} style={{ ...btnSmall, color: B.gold, borderColor: B.gold + "60" }}>📊 Analytics</button>
        <button data-testid="admin-home-btn" onClick={onHome} style={{ ...btnSmall, color: B.whiteGray }}>Home</button>
        <button data-testid="admin-logout-btn" onClick={onLogout} style={{ ...btnSmall, color: B.red, borderColor: B.red + "40" }}>Logout</button>
      </div>
    </div>
  );
}

// ── Stats row (Total / per-course / Admins) ────────────────────────────
// `budgetStatus` (optional): {tripped, remaining_seconds} — when tripped,
// a small red dot appears on the "Total Users" tile. Tooltip explains why.
export function StatsBar({ users, budgetStatus }) {
  const stats = [
    { label: "Total Users", value: users.length, color: B.white, alert: !!budgetStatus?.tripped },
    { label: "Beginner Access", value: users.filter(u => u.course_access?.includes("beginner")).length, color: B.blue },
    { label: "Advanced Access", value: users.filter(u => u.course_access?.includes("advanced")).length, color: B.gold },
    { label: "CFD Access", value: users.filter(u => u.course_access?.includes("cfd")).length, color: "#00D4AA" },
    { label: "Commodities", value: users.filter(u => u.course_access?.includes("commodities")).length, color: "#E8883A" },
    { label: "Roadmap", value: users.filter(u => u.course_access?.includes("roadmap")).length, color: "#9B59B6" },
    { label: "Admins", value: users.filter(u => u.role === "admin").length, color: B.green },
  ];
  const minLeft = budgetStatus?.tripped ? Math.ceil((budgetStatus.remaining_seconds || 0) / 60) : 0;
  return (
    <div style={{ display: "flex", gap: 16, padding: "20px 24px", flexWrap: "wrap" }}>
      {stats.map((s) => (
        <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}
          style={{ position: "relative", background: B.card, border: `1px solid ${B.border}`, borderRadius: 12, padding: "16px 24px", minWidth: 140 }}>
          {s.alert && (
            <span data-testid="budget-alert-dot"
              title={`LLM budget exhausted — warmer paused for ~${minLeft}m. Top up Universal Key in Profile.`}
              style={{
                position: "absolute", top: 8, right: 8, width: 10, height: 10, borderRadius: 999,
                background: B.red, boxShadow: `0 0 0 3px ${B.red}33, 0 0 8px ${B.red}aa`,
                animation: "pulse 1.4s ease-in-out infinite",
              }} />
          )}
          <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 11, color: B.gray, letterSpacing: 1, fontFamily: "monospace" }}>{s.label}</div>
        </div>
      ))}
      {budgetStatus?.tripped && (
        <div data-testid="budget-alert-banner" style={{
          flex: "1 1 100%", background: `${B.red}10`, border: `1px solid ${B.red}55`,
          borderLeft: `4px solid ${B.red}`, borderRadius: 10, padding: "10px 16px",
          color: B.red, fontSize: 12, fontWeight: 600, fontFamily: "monospace", letterSpacing: 0.5,
        }}>
          ⚠ LLM BUDGET EXHAUSTED — macro warmer paused for ~{minLeft}m. Top up via Profile → Universal Key → Add Balance. Cache is serving stale data; users see a friendly banner.
        </div>
      )}
    </div>
  );
}

// ── Tab strip — permission-aware ───────────────────────────────────────
export function AdminTabs({ user, tab, setTab, setSelectedUser, notifsCount }) {
  const isOwner = !!user?.is_owner;
  const perms = new Set(user?.admin_permissions || []);
  const allTabs = [
    { id: "users", label: "User Management", needs: "users" },
    { id: "progress", label: "Progress Viewer", needs: "progress" },
    { id: "axel", label: "AXEL Usage", ownerOnly: true },
    { id: "router", label: "AXEL Router", ownerOnly: true },
    { id: "system", label: "System Health", ownerOnly: true },
    { id: "bridge", label: "MT5 Bridge", adminOk: true },
    { id: "admins", label: "Admins", ownerOnly: true },
    { id: "notifications", label: `Notifications (${notifsCount})`, needs: "notifications" },
  ];
  const visible = allTabs.filter(t => isOwner || t.adminOk || (t.ownerOnly ? false : perms.has(t.needs)));
  return (
    <div style={{ display: "flex", gap: 0, margin: "0 24px", borderRadius: 8, overflow: "hidden", border: `1px solid ${B.border}`, width: "fit-content" }}>
      {visible.map(t => (
        <button key={t.id} data-testid={`tab-${t.id}`}
          onClick={() => { setTab(t.id); setSelectedUser(null); }}
          style={{ padding: "8px 20px", border: "none", cursor: "pointer",
            background: tab === t.id ? B.gold + "20" : "transparent",
            color: tab === t.id ? B.gold : B.gray, fontSize: 13, fontWeight: 600 }}>
          {t.label}
          {t.ownerOnly && <span style={{ marginLeft: 6, fontSize: 9, color: B.gold, opacity: 0.7 }}>OWNER</span>}
        </button>
      ))}
    </div>
  );
}

// ── Toast ──────────────────────────────────────────────────────────────
export function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div data-testid="admin-toast" style={{
      position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 10000,
      background: B.card, border: `1px solid ${toast.color}40`, borderRadius: 10,
      padding: "12px 24px", color: toast.color, fontSize: 13, fontWeight: 600,
      boxShadow: `0 8px 30px ${toast.color}15`,
    }}>{toast.msg}</div>
  );
}

// ── Notifications list ─────────────────────────────────────────────────
export function NotificationsList({ notifs }) {
  if (notifs.length === 0) {
    return (
      <div data-testid="notifications-list" style={{ padding: "16px 24px 40px", maxWidth: 800 }}>
        <p style={{ color: B.gray, padding: 16 }}>No notifications yet. Grant or revoke access to generate notifications.</p>
      </div>
    );
  }
  return (
    <div data-testid="notifications-list" style={{ padding: "16px 24px 40px", maxWidth: 800 }}>
      {notifs.map((n, i) => {
        const granted = n.type === "access_granted";
        const c = granted ? B.green : B.red;
        return (
          <div key={n._id || n.id || `notif-${i}`} style={{
            background: B.card, border: `1px solid ${c}30`,
            borderRadius: 10, padding: "12px 18px", marginBottom: 8,
            borderLeft: `3px solid ${c}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ color: B.white, fontWeight: 600, fontSize: 13 }}>{n.user_name || n.user_email}</span>
                <span style={{ color: c, fontSize: 12, marginLeft: 8 }}>{granted ? "GRANTED" : "REVOKED"}</span>
                <span style={{ color: B.whiteGray, fontSize: 12, marginLeft: 8 }}>{n.course_label}</span>
              </div>
              <span style={{ color: B.gray, fontSize: 10 }}>{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</span>
            </div>
            <div style={{ color: B.gray, fontSize: 12, marginTop: 4 }}>{n.message}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── A single user row in the user-management list ──────────────────────
export function UserCard({ u, selectedUser, isSelected, onSelectToggle, onToggleAccess, onTogglePmir, onToggleIntel, onToggleQuantBeta, onView, onRevoke, onDelete }) {
  const isAdmin = u.role === "admin";
  // Admins always bypass PMIR + Intel gates by design (see backend
  // require_intel_access). Reflect this visually so the row doesn't look
  // like the user "has no Intel access" when in reality they always do.
  const pmirEffective  = !!u.pmir_access  || isAdmin;
  const intelEffective = !!u.intel_access || isAdmin;
  const quantBetaEffective = !!u.axel_quant_beta_enabled || isAdmin;
  return (
    <div data-testid={`user-row-${u.email}`} style={{
      background: B.card, border: `1px solid ${selectedUser?.user?._id === u._id ? B.gold + "40" : (isSelected ? "#f472b6" + "60" : B.border)}`,
      borderRadius: 10, padding: "14px 18px", marginBottom: 8,
      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
    }}>
      <div style={{ flex: "1 1 200px", display: "flex", alignItems: "flex-start", gap: 10 }}>
        {onSelectToggle && (
          <input type="checkbox" data-testid={`select-user-${u.email}`}
            checked={!!isSelected} onChange={() => onSelectToggle(u._id)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginTop: 3, accentColor: "#f472b6", width: 16, height: 16, cursor: "pointer" }}
            aria-label={`Select ${u.email} for bulk INTEL action`} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: B.white, fontWeight: 600, fontSize: 14 }}>{u.name || "Unnamed"}</span>
            {isAdmin && <Pill active color={B.green}>ADMIN</Pill>}
          </div>
          <div style={{ color: B.gray, fontSize: 12, marginTop: 2 }}>{u.email}</div>
          <div style={{ color: B.gray, fontSize: 10, marginTop: 2 }}>
            Joined: {u.created_at ? new Date(u.created_at).toLocaleDateString() : "N/A"}
            {u.last_login ? ` | Last login: ${new Date(u.last_login).toLocaleDateString()}` : ""}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {COURSES.map(c => (
          <Pill key={c} active={u.course_access?.includes(c)} color={COURSE_COLOR[c]}
            onClick={() => onToggleAccess(u._id, c)}>{COURSE_LABEL[c]}</Pill>
        ))}
        <Pill active={pmirEffective} color={"#38bdf8"}
          onClick={() => onTogglePmir(u._id)} data-testid={`pmir-toggle-${u.email}`}
          title={isAdmin && !u.pmir_access ? "Auto-enabled via ADMIN role" : ""}>
          {isAdmin && !u.pmir_access ? "PMIR ✦" : "PMIR"}
        </Pill>
        <Pill active={intelEffective} color={"#f472b6"}
          onClick={() => onToggleIntel(u._id)} data-testid={`intel-toggle-${u.email}`}
          title={isAdmin && !u.intel_access ? "Auto-enabled via ADMIN role" : ""}>
          {isAdmin && !u.intel_access ? "INTEL ✦" : "INTEL"}
        </Pill>
        <Pill active={quantBetaEffective} color={"#F5A623"}
          onClick={() => onToggleQuantBeta && onToggleQuantBeta(u._id)}
          data-testid={`quant-beta-toggle-${u.email}`}
          title={isAdmin && !u.axel_quant_beta_enabled ? "Auto-enabled via ADMIN role" : "AXEL Quant Lab beta access"}>
          {isAdmin && !u.axel_quant_beta_enabled ? "QUANT ✦" : "QUANT"}
        </Pill>
        <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
          <button data-testid={`view-progress-${u.email}`} onClick={() => onView(u._id)}
            style={{ ...btnTiny, color: B.blue, borderColor: B.blue + "40" }}>Progress</button>
          {u.role !== "admin" && (
            <>
              <button data-testid={`revoke-${u.email}`} onClick={() => onRevoke(u._id)}
                style={{ ...btnTiny, color: B.gold, borderColor: B.gold + "40" }}>Kick</button>
              <button data-testid={`delete-${u.email}`} onClick={() => onDelete(u._id)}
                style={{ ...btnTiny, color: B.red, borderColor: B.red + "40" }}>Delete</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Intel Lock Control — global toggle + counts ────────────────────────
// Renders a master switch for the AXEL Intel Hub. When enabled, /intel is
// gated for ALL non-admin users by default; individual users can be granted
// access via the per-user INTEL pill. Designed for future monetization.
export function IntelLockControl({ lockState, onToggle, busy }) {
  if (!lockState) return null;
  const { enabled, unlocked_count, total_non_admin_users } = lockState;
  return (
    <div data-testid="intel-lock-control" style={{
      margin: "0 24px 16px", padding: "14px 18px",
      background: enabled ? `${"#f472b6"}10` : B.card,
      border: `1px solid ${enabled ? "#f472b6" + "60" : B.border}`,
      borderLeft: `4px solid ${enabled ? "#f472b6" : B.gray}`,
      borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between",
      flexWrap: "wrap", gap: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 22 }}>{enabled ? "🔒" : "🔓"}</span>
        <div>
          <div style={{ fontSize: 12, letterSpacing: 1.5, color: enabled ? "#f472b6" : B.green, fontFamily: "monospace", fontWeight: 700 }}>
            AXEL INTEL HUB · {enabled ? "GLOBAL LOCK ON" : "OPEN TO ALL USERS"}
          </div>
          <div style={{ color: B.whiteGray, fontSize: 12, marginTop: 4 }}>
            {enabled
              ? <>Only <b style={{ color: "#f472b6" }}>{unlocked_count}</b> of {total_non_admin_users} users have access. Use the <b style={{ color: "#f472b6" }}>INTEL</b> pill on a user row to grant individual access.</>
              : <>Every signed-in user can use the Intel Hub. Toggle the lock to convert it into a paid / funded-only feature.</>}
          </div>
        </div>
      </div>
      <button data-testid="intel-lock-toggle-btn" onClick={onToggle} disabled={busy}
        style={{
          padding: "10px 22px", borderRadius: 8, fontSize: 12, fontWeight: 700,
          letterSpacing: 1, fontFamily: "monospace", cursor: busy ? "wait" : "pointer",
          background: enabled ? "transparent" : "#f472b6",
          border: enabled ? `1.5px solid #f472b6` : "none",
          color: enabled ? "#f472b6" : "#000",
        }}>
        {busy ? "…" : enabled ? "UNLOCK FOR ALL" : "LOCK INTEL"}
      </button>
    </div>
  );
}

// ── User list panel (scrollable) ───────────────────────────────────────
export function UserListSection({ loading, filteredUsers, selectedUser, selectedIds, handlers }) {
  return (
    <div style={{ flex: "1 1 600px", maxHeight: "60vh", overflowY: "auto" }}>
      {loading ? (
        <p style={{ color: B.gray }}>Loading...</p>
      ) : filteredUsers.length === 0 ? (
        <p style={{ color: B.gray }}>No users found</p>
      ) : (
        filteredUsers.map(u => (
          <UserCard key={u._id} u={u} selectedUser={selectedUser}
            isSelected={selectedIds?.has(u._id)}
            onSelectToggle={handlers.onSelectToggle}
            onToggleAccess={handlers.onToggleAccess}
            onTogglePmir={handlers.onTogglePmir}
            onToggleIntel={handlers.onToggleIntel}
            onToggleQuantBeta={handlers.onToggleQuantBeta}
            onView={handlers.onView}
            onRevoke={handlers.onRevoke}
            onDelete={handlers.onDelete} />
        ))
      )}
    </div>
  );
}

// ── Bulk INTEL action bar (sticky banner above the user list) ──────────
// Renders only when at least one user is checkbox-selected. Lets the admin
// grant or revoke INTEL access for the whole batch in one click — useful
// for onboarding a list of paying / funded-account members at once.
export function BulkIntelBar({ selectedIds, filteredUsers, onGrant, onRevoke, onClear, onSelectAllVisible, busy }) {
  const count = selectedIds.size;
  const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u._id));
  return (
    <div data-testid="bulk-intel-bar" style={{
      marginBottom: 10, padding: "10px 14px",
      background: count > 0 ? `${"#f472b6"}10` : B.card,
      border: `1px solid ${count > 0 ? "#f472b6" + "55" : B.border}`,
      borderLeft: `3px solid ${count > 0 ? "#f472b6" : B.gray}`,
      borderRadius: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    }}>
      <button data-testid="bulk-select-all-visible-btn"
        onClick={() => onSelectAllVisible(!allVisibleSelected)}
        style={{ ...btnTiny, color: "#f472b6", borderColor: "#f472b6" + "55", padding: "5px 11px" }}>
        {allVisibleSelected ? "Clear visible" : `Select all visible (${filteredUsers.length})`}
      </button>
      <span data-testid="bulk-selected-count" style={{
        color: count > 0 ? "#f472b6" : B.gray, fontSize: 12, fontFamily: "monospace", fontWeight: 700,
      }}>
        {count > 0 ? `${count} selected` : "No users selected"}
      </span>
      <div style={{ flex: 1 }} />
      <button data-testid="bulk-grant-intel-btn" onClick={onGrant} disabled={busy || count === 0}
        style={{
          padding: "7px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700,
          letterSpacing: 1, fontFamily: "monospace",
          cursor: (busy || count === 0) ? "not-allowed" : "pointer",
          opacity: (busy || count === 0) ? 0.4 : 1,
          background: "#f472b6", border: "none", color: "#000",
        }}>
        ✦ GRANT INTEL TO {count || 0}
      </button>
      <button data-testid="bulk-revoke-intel-btn" onClick={onRevoke} disabled={busy || count === 0}
        style={{
          padding: "7px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700,
          letterSpacing: 1, fontFamily: "monospace",
          cursor: (busy || count === 0) ? "not-allowed" : "pointer",
          opacity: (busy || count === 0) ? 0.4 : 1,
          background: "transparent", border: `1.5px solid ${B.red}`, color: B.red,
        }}>
        REVOKE FROM {count || 0}
      </button>
      {count > 0 && (
        <button data-testid="bulk-clear-btn" onClick={onClear}
          style={{ ...btnTiny, color: B.gray, padding: "5px 10px" }}>Clear</button>
      )}
    </div>
  );
}

// ── Per-tier progress bars (Flexx LessonsHub) ──────────────────────────
function FlexxTierBars({ flexx }) {
  const tiers = [
    { id: "1", label: "Tier 1 · Foundations", total: 27, color: B.blue },
    { id: "2", label: "Tier 2 · SuPER System", total: 25, color: B.gold },
    { id: "3", label: "Tier 3 · AI Trader",   total: 25, color: "#00D4AA" },
    { id: "4", label: "Tier 4 · Algo / MT5",  total: 25, color: "#E8883A" },
    { id: "5", label: "Tier 5 · Quant",       total: 30, color: "#9B59B6" },
  ];
  return (
    <div data-testid="flexx-tier-breakdown" style={{ marginBottom: 12 }}>
      {tiers.map((t) => {
        const done = (flexx.tier_breakdown || {})[t.id] || 0;
        const pct = Math.min(100, (done / t.total) * 100);
        const graduated = !!(flexx.tier_grads || {})[`tier${t.id}`];
        return (
          <div key={t.id} style={{ marginBottom: 6 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: 10, fontFamily: "monospace",
              color: graduated ? t.color : B.whiteGray, marginBottom: 2,
            }}>
              <span>{t.label} {graduated && "🏆"}</span>
              <span>{done}/{t.total}</span>
            </div>
            <div style={{ height: 6, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: t.color, transition: "width 0.4s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── FLEXX block (tier progress + completed lessons + quiz attempts) ────
function FlexxProgressBlock({ flexx }) {
  return (
    <div data-testid="admin-flexx-progress" style={{
      marginBottom: 18, background: B.bg, borderRadius: 8, padding: 14,
      border: `1px solid ${B.gold}30`,
    }}>
      <div style={{
        fontSize: 11, color: B.gold, letterSpacing: 1.5,
        fontFamily: "monospace", marginBottom: 10, fontWeight: 700,
      }}>
        🎓 TIER PROGRESS · LESSONS HUB
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 10, marginBottom: 12,
      }}>
        <div data-testid="flexx-xp-stat">
          <div style={{ fontSize: 22, color: B.gold, fontWeight: 800, fontFamily: "monospace" }}>
            {(flexx.xp || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 10, color: B.gray, letterSpacing: 1 }}>TOTAL XP</div>
        </div>
        <div data-testid="flexx-completed-stat">
          <div style={{ fontSize: 22, color: B.white, fontWeight: 800, fontFamily: "monospace" }}>
            {flexx.completed_count || 0}
          </div>
          <div style={{ fontSize: 10, color: B.gray, letterSpacing: 1 }}>LESSONS DONE</div>
        </div>
        <div data-testid="flexx-grads-stat">
          <div style={{ fontSize: 22, color: "#00D4AA", fontWeight: 800, fontFamily: "monospace" }}>
            {Object.values(flexx.tier_grads || {}).filter(Boolean).length}
          </div>
          <div style={{ fontSize: 10, color: B.gray, letterSpacing: 1 }}>TIERS GRADUATED</div>
        </div>
      </div>

      <FlexxTierBars flexx={flexx} />

      {(flexx.completed_lessons || []).length > 0 && (
        <details data-testid="flexx-last-lessons">
          <summary style={{
            cursor: "pointer", fontSize: 10, color: B.gold,
            letterSpacing: 1.2, fontFamily: "monospace", padding: "4px 0",
          }}>
            ▸ LAST {Math.min(10, flexx.completed_lessons.length)} LESSONS COMPLETED
          </summary>
          <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
            {flexx.completed_lessons.slice(0, 10).map((l) => (
              <div key={l.lesson_id} style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 11, fontFamily: "monospace", color: B.whiteGray,
                padding: "3px 0", borderBottom: `1px dashed ${B.border}`,
              }}>
                <span>{l.perfect_ever ? "💎" : "✓"} Lesson {l.lesson_id}</span>
                <span style={{ color: B.gray }}>
                  {Math.round((l.score || 0) * 100)}% · {l.completed_at_ms ? new Date(l.completed_at_ms).toLocaleDateString() : "—"}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {(flexx.recent_attempts || []).length > 0 && (
        <details data-testid="flexx-recent-attempts" style={{ marginTop: 8 }}>
          <summary style={{
            cursor: "pointer", fontSize: 10, color: B.gold,
            letterSpacing: 1.2, fontFamily: "monospace", padding: "4px 0",
          }}>
            ▸ LAST {Math.min(10, flexx.recent_attempts.length)} QUIZ ATTEMPTS (incl. retries)
          </summary>
          <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
            {flexx.recent_attempts.slice(0, 10).map((a) => (
              <div key={`${a.lesson_id}-${a.created_at || a.id || Math.random()}`} style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 11, fontFamily: "monospace", color: B.whiteGray,
                padding: "3px 0", borderBottom: `1px dashed ${B.border}`,
              }}>
                <span>Lesson {a.lesson_id}</span>
                <span style={{ color: (a.score || 0) >= 0.7 ? "#00D4AA" : B.gray }}>
                  {Math.round((a.score || 0) * 100)}% · {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {flexx.state_updated_at && (
        <div style={{ fontSize: 9, color: B.gray, marginTop: 8, fontFamily: "monospace" }}>
          Last synced: {new Date(flexx.state_updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ── Legacy progress block (db.progress course-style) ──────────────────
function LegacyProgressBlock({ progress }) {
  return (
    <>
      <div style={{
        fontSize: 11, color: B.gold, letterSpacing: 1, fontFamily: "monospace",
        marginBottom: 8, opacity: 0.8,
      }}>
        LEGACY COURSE PROGRESS
      </div>
      {progress.map((p) => (
        <div key={p.course_id} style={{ marginBottom: 16, background: B.bg, borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COURSE_COLOR[p.course_id] || B.gold, marginBottom: 8, textTransform: "capitalize" }}>
            {p.course_id} Course
          </div>
          <div style={{ fontSize: 12, color: B.whiteGray }}>
            Completed: <b>{Object.keys(p.completed || {}).filter(k => p.completed[k]).length}</b> lessons
          </div>
          <div style={{ fontSize: 12, color: B.whiteGray }}>
            Notes: <b>{Object.keys(p.notes || {}).filter(k => p.notes[k]).length}</b> saved
          </div>
          {p.updated_at && <div style={{ fontSize: 10, color: B.gray, marginTop: 4 }}>Last update: {new Date(p.updated_at).toLocaleString()}</div>}
        </div>
      ))}
    </>
  );
}

// ── Selected-user progress panel (right side) ──────────────────────────
export function ProgressPanel({ selectedUser, onClose }) {
  if (!selectedUser) return null;
  const flexx = selectedUser.flexx;
  const hasLegacy = (selectedUser.progress || []).length > 0;

  return (
    <div data-testid="progress-panel" style={{
      flex: "0 0 340px", background: B.card, border: `1px solid ${B.border}`,
      borderRadius: 12, padding: 20, maxHeight: "60vh", overflowY: "auto", position: "sticky", top: 20,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: B.white }}>{selectedUser.user?.name}</h3>
        <button onClick={onClose} style={{ ...btnTiny, color: B.gray }}>Close</button>
      </div>
      <div style={{ color: B.gray, fontSize: 12, marginBottom: 16 }}>{selectedUser.user?.email}</div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: B.gold, letterSpacing: 1, fontFamily: "monospace", marginBottom: 8 }}>COURSE ACCESS</div>
        <div style={{ display: "flex", gap: 6 }}>
          {COURSES.map(c => (
            <Pill key={c} active={selectedUser.user?.course_access?.includes(c)} color={COURSE_COLOR[c]}>{COURSE_LABEL[c]}</Pill>
          ))}
        </div>
      </div>

      {flexx && <FlexxProgressBlock flexx={flexx} />}

      {hasLegacy && <LegacyProgressBlock progress={selectedUser.progress} />}

      {!hasLegacy && !flexx?.completed_count && (
        <div style={{ color: B.gray, fontSize: 12, fontStyle: "italic" }}>No progress data yet</div>
      )}
    </div>
  );
}
